import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, onAuthStateChanged, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { doc, collection, writeBatch, serverTimestamp, getDoc } from 'firebase/firestore';
import { FcGoogle } from 'react-icons/fc';
import { PiShieldCheck } from 'react-icons/pi';
import { auth, db } from '../firebaseConfig';
import '../styles/Auth.css';

function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user && !loading) {
        navigate('/dashboard', { replace: true });
      }
    });
    return () => unsub();
  }, [navigate, loading]);

  const provisionNewOrg = async (user) => {
    // Create Org directly in Firestore
    const orgRef = doc(collection(db, 'orgs'));
    const orgId = orgRef.id;

    const domain = user.email?.split('@')[1]?.toLowerCase();
    const orgName = domain ? `${domain} Organization` : 'My Organization';

    // Batch write for atomicity
    const batch = writeBatch(db);

    // 1. Create Org
    batch.set(orgRef, {
      name: orgName,
      domain: domain || null,
      createdBy: user.uid,
      createdAt: serverTimestamp(),
    });

    // 2. Add User as Admin
    const memberRef = doc(db, 'orgs', orgId, 'members', user.uid);
    batch.set(memberRef, {
      uid: user.uid,
      email: user.email || null,
      role: 'admin',
      status: 'active',
      joinedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // 3. Link User to Org
    const userRef = doc(db, 'users', user.uid);
    batch.set(userRef, {
      uid: user.uid,
      email: user.email,
      orgId: orgId,
      createdAt: serverTimestamp(),
    }, { merge: true });

    await batch.commit();
  };

  const ensureProvisionedUser = async (user) => {
    const userDocRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userDocRef);
    const userData = userDoc.exists() ? userDoc.data() : null;

    if (!userData?.orgId) {
      await provisionNewOrg(user);
    }
  };

  const handleGoogleSignup = async () => {
    setLoading(true);
    setError('');
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      await ensureProvisionedUser(result.user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await provisionNewOrg(cred.user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <Link to="/" className="auth-brand">UpConvert</Link>

      <div className="auth-card">
        <div className="auth-header">
          <p className="eyebrow">Start closing</p>
          <h1>Create your account</h1>
          <p className="subhead">Start your 30-day free trial. Setup takes under 2 minutes.</p>
        </div>

        <div className="auth-form">
          <button
            type="button"
            className="google-button"
            onClick={handleGoogleSignup}
            disabled={loading}
          >
            <FcGoogle size={20} />
            Sign up with Google
          </button>

          <div className="divider">
            <span>or sign up with email</span>
          </div>

          <form onSubmit={handleSignup} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <label className="input-label">
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </label>

            <label className="input-label">
              Password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password"
                required
              />
            </label>

            <label className="input-label">
              Confirm password
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                required
              />
            </label>

            {error && <p className="error-text">{error}</p>}

            <button type="submit" className="primary-button" disabled={loading}>
              {loading ? 'Creating account…' : 'Start your free trial'}
            </button>
          </form>

          <p className="auth-footer">
            Already have an account? <Link to="/login">Log in</Link>
          </p>

          <div className="auth-trust">
            <PiShieldCheck size={16} color="var(--accent-base)" />
            Enterprise-grade encryption
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup;
